<template>
  <vue-good-table 
    v-bind="tableOptions"
    styleClass="vgt-table condensed"
    :columns="columns"
    :rows="rows"
    :search-options="searchOptions"
    :pagination-options="paginationOptions"
    :row-click="true"
    @row-click="onRowClick"
    compact-mode>
      <template #table-row="props">
        <div v-if="props.column.field == 'Nm'" class="d-flex align-items-center gap-2">
          <div
            class="d-flex align-items-center justify-content-center flex-shrink-0 asset-logo"
            :class="logoClass(props.row)"
          >
            <img
              v-if="showShareLogo(props.row)"
              :src="shareLogoUrl(props.row.ISIN)"
              :alt="props.row.Nm"
              @error="onLogoError(props.row.ISIN)"
            />
            <span v-else>{{ shareMonogram(props.row.Nm) }}</span>
          </div>
          <button
            v-if="props.row.ISINID"
            type="button"
            class="forum-bubble-btn"
            :class="{ 'forum-bubble-btn-dark': isDarkMode }"
            :title="'Fórum: ' + props.row.Nm"
            @click.stop="openShareForum(props.row)"
          >
            <fa-icon icon="fa-regular fa-comment-dots" />
          </button>
          <div class="d-flex flex-column min-w-0">
            <span class="mb-0 text-md text-dark font-weight-bold text-truncate">{{ props.row.Nm }}</span>
            <span class="text-xs font-monospace">{{ props.row.ISIN }}</span>
          </div>
        </div>
        <div v-else-if="props.column.field == 'PrcDf'" class="d-flex flex-column align-items-end">
          <span class="mb-1 text-md font-weight-bold" :class="changeClass(props.row.PrcDf)">
            {{ formatChangeTypeNumber(props.row.PrcDf) }}
          </span>
          <span class="text-xs" :class="changeClass(props.row.PctDf)">
            {{ formatChangeTypeNumber(props.row.PctDf) }}%
          </span>
        </div>
        <div v-else-if="props.column.field == 'action'" class="d-flex justify-content-end align-items-center gap-1">
         <button
            v-if="hasOpenPosition(props.row)"
            type="button"
            class="trade-action-btn trade-action-close"
            :class="{ 'trade-action-btn-dark': isDarkMode }"
            title="Uzavřít pozici"
            @click.stop="emit('action', { ISINID: props.row.ISINID, closePosition: true})"
          >
            <fa-icon icon="fa-solid fa-file-export" />
          </button>
          <template v-else>
            <button
              type="button"
              class="trade-action-btn trade-action-long"
              :class="{
                'trade-action-btn-dark': isDarkMode,
                'is-disabled': cannotAfford(props.row)
              }"
              :disabled="cannotAfford(props.row)"
              :title="cannotAfford(props.row)
                ? 'Nedostatek volného kapitálu na 1 kus'
                : 'Otevřít LONG'"
              @click.stop="openTrade(props.row, 'long')"
            >
              <fa-icon icon="fa-solid fa-circle-chevron-up" />
            </button>
            <button
              type="button"
              class="trade-action-btn trade-action-short"
              :class="{
                'trade-action-btn-dark': isDarkMode,
                'is-disabled': cannotAfford(props.row)
              }"
              :disabled="cannotAfford(props.row)"
              :title="cannotAfford(props.row)
                ? 'Nedostatek volného kapitálu na 1 kus'
                : 'Otevřít SHORT'"
              @click.stop="openTrade(props.row, 'short')"
            >
              <fa-icon icon="fa-solid fa-circle-arrow-down" />
            </button>
          </template>
        </div>
        <span v-else class="num-cell">
          {{props.formattedRow[props.column.field]}}
        </span>
      </template>
      <template #emptystate>
      <div class="text-center">
        <span>Žádná aktiva</span>
      </div>
    </template>
  </vue-good-table>
</template>

<script setup>
import { reactive, computed } from 'vue';
import { useStore } from 'vuex';
import { tableOptions, tableSearchOptions } from '../../../../helpers/tableOptions';
import { shareLogoUrl, shareMonogram } from '@/helpers/shareLogos';

const props = defineProps({
  rows: Array,
  availableCapital: {
    type: Number,
    default: null
  }
})
const emit = defineEmits(['action']);
const store = useStore();
const isDarkMode = computed(() => !!store.state.isDarkMode);
const logoFailed = reactive({});

const onLogoError = (isin) => {
  if (isin) logoFailed[isin] = true;
};

const showShareLogo = (row) => !!(shareLogoUrl(row.ISIN) && !logoFailed[row.ISIN]);

const logoClass = (row) => {
  if (!showShareLogo(row)) return 'asset-logo-fallback';
  return '';
};

const hasOpenPosition = (row) => {
  const amt = row?.Amt;
  if (amt == null || amt === '') return false;
  const n = Number(amt);
  return !Number.isNaN(n) && n !== 0;
};

const sharePrice = (row) => {
  const n = Number(String(row?.Prc ?? '').replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

const cannotAfford = (row) => {
  if (props.availableCapital == null) return false;
  const price = sharePrice(row);
  return price > 0 && price > props.availableCapital;
};

const openTrade = (row, side) => {
  if (!row?.ISINID || cannotAfford(row)) return;
  emit('action', { ISINID: row.ISINID, side });
};

const searchOptions = {
  ...tableSearchOptions,
  placeholder: 'Hledat aktivum…'
};

const paginationOptions = computed(() => ({
  ...tableOptions.paginationOptions,
  enabled: true,
  perPage: 10,
  perPageDropdown: [10, 25],
  dropdownAllowAll: true,
  allLabel: 'VŠE',
}));

const formatFn = (value) => {
  return store.getters.formatTableNumber(value);
};
const formatChangeTypeNumber = (value) => {
  return store.getters.formatChangeTypeNumber(value);
};
const changeClass = (value) => {
  const n = Number(String(value).replace(',', '.').replace(/\s/g, ''));
  if (Number.isNaN(n) || n === 0) return '';
  return n > 0 ? 'text-success' : 'text-danger';
};

const onRowClick = (params) => {
  if (params.row.ISINID) {
    store.dispatch('fetchISINChartData', params.row);
  }
};

const openShareForum = (row) => {
  if (!row?.ISINID) return;
  store.dispatch('openForum', {
    title: row.Nm || 'Akcie',
    ISINID: row.ISINID,
    ISIN: row.ISIN
  });
};

const columns = reactive([
  { 
    label: 'Aktivum',
    field: 'Nm',
    thClass: 'text-uppercase text-dark text-xxs font-weight-bolder',
    tdClass: 'text-sm font-weight-bold align-middle',
  },
  { 
    label: 'Pozice',
    field: 'Amt',
    type: 'number',
    thClass: 'text-uppercase text-dark text-xxs font-weight-bolder text-end',
    tdClass: 'text-sm font-weight-bold text-end align-middle',
  },
  {
    label: 'Cena',
    field: 'Prc',
    type: 'number',
    tooltip: 'Aktuální cena',
    thClass: 'text-uppercase text-dark text-xxs font-weight-bolder text-end',
    tdClass: 'text-sm font-weight-bold text-end align-middle',
    formatFn: formatFn,
  },
  {
    label: 'Průměr',
    field: 'AvgPrc',
    type: 'number',
    tooltip: 'Průměrná nákupní cena otevřené pozice',
    thClass: 'text-uppercase text-dark text-xxs font-weight-bolder text-end',
    tdClass: 'text-sm font-weight-bold text-end align-middle',
    formatFn: formatFn,
  },
  {
    label: 'Změna',
    field: 'PrcDf',
    type: 'number',
    tooltip: 'Denní změna kurzu',
    thClass: 'text-uppercase text-dark text-xxs font-weight-bolder text-end',
    tdClass: 'text-sm font-weight-bold text-end align-middle',
  },
  {
    label: 'Pokyn',
    thClass: 'text-uppercase text-dark text-xxs font-weight-bolder text-end',
    tdClass: 'align-middle td-middle',
    field: 'action',
    sortable: false,
  },
  {
    label: 'ISIN',
    field: 'ISIN',
    hidden: true,
  }]
);

</script>

<style scoped>
.asset-logo {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  overflow: hidden;
}

.asset-logo img {
  width: 28px;
  height: 28px;
  object-fit: contain;
}

.asset-logo-fallback {
  background: rgba(100, 116, 139, 0.16);
  color: #64748b;
  font-size: 0.62rem;
  font-weight: 700;
}

.forum-bubble-btn {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 1.5px solid #CBD5E1;
  background: transparent;
  color: #64748B;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  flex-shrink: 0;
  font-size: 0.7rem;
}

.forum-bubble-btn:hover {
  color: #FF8A4C;
  border-color: #FF8A4C;
  background: rgba(255, 138, 76, 0.08);
}

.forum-bubble-btn-dark {
  border-color: #475569;
  color: #94A3B8;
}

.forum-bubble-btn-dark:hover {
  color: #FF8A4C;
  border-color: #FF8A4C;
  background: rgba(255, 138, 76, 0.14);
}

.trade-action-btn {
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 0;
  background: transparent;
  color: #64748B;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  flex-shrink: 0;
  font-size: 1.35rem;
  line-height: 1;
}

.trade-action-btn-dark {
  color: #94A3B8;
}

.trade-action-close:hover,
.trade-action-btn-dark.trade-action-close:hover {
  color: #EF4444;
  background: transparent;
}

.trade-action-long:hover,
.trade-action-btn-dark.trade-action-long:hover {
  color: #10B981;
  background: transparent;
}

.trade-action-short:hover,
.trade-action-btn-dark.trade-action-short:hover {
  color: #FF8A4C;
  background: transparent;
}

.trade-action-btn:disabled,
.trade-action-btn.is-disabled,
.trade-action-btn-dark:disabled,
.trade-action-btn-dark.is-disabled {
  color: #94A3B8;
  opacity: 0.35;
  cursor: not-allowed;
}

.trade-action-btn:disabled:hover,
.trade-action-btn.is-disabled:hover,
.trade-action-btn-dark:disabled:hover,
.trade-action-btn-dark.is-disabled:hover {
  color: #94A3B8;
  background: transparent;
}

:deep(td.td-middle) {
  vertical-align: middle !important;
}

.num-cell {
  display: block;
  width: 100%;
  text-align: right;
  white-space: nowrap;
}
</style>
